// Messages — unified inbox: Customers / Suppliers / Team tabs with threaded
// conversations, channel-tagged bubbles, compose modal, and 3-second polling.
//
// Source-of-truth reference: /var/www/kitchenos/portal-standalone.html
//   page section:    line 2529  (#page-messages)
//   render fn:       line 11848 (window.renderMessagesPage)
//   hydrate fn:      line 11733 (window.__kosMsgHydrateFromServer)
//   thread grouping: line 11814 (getThreadedMessages — by subject)
//   delete thread:   line 12163, 12197 (msgDeleteThread / msgDeleteMessage)
//   compose modal:   line 12352 (msgShowCompose)
//
// Backend (real, in /var/www/kitchenos-api/server.js):
//   POST   /api/portal/messages                             — server.js:1317  (send / compose)
//   GET    /api/portal/messages?retailerId=&supplierId=     — server.js:1408  (list threads)
//   POST   /api/portal/messages/mark-read   { threadId }    — server.js:1442
//   DELETE /api/portal/messages/:id                         — server.js:1452  (single message)
//   DELETE /api/portal/messages/thread/:threadId            — server.js:1468  (whole thread)
//   POST   /api/portal/customer-message                     — server.js:1486  (retailer → customer)
//
// Notes / gaps:
// - There is NO single-thread GET endpoint and NO "team" endpoint. We hold
//   all threads in memory after the list call and filter client-side.
// - "Reply" reuses POST /api/portal/messages with `inReplyTo` for suppliers,
//   and POST /api/portal/customer-message for customers. Team replies are
//   stubbed (no backend) — toast informs the user.
// - The unified store returns thread objects via `data.threads` (retailer
//   POV) and `data.supplierThreads` (when called with a supplierId). For
//   the retailer we use `threads` + `supplierThreads`. We split by direction
//   to assign Customers vs Suppliers — see `classifyTab`.
// - Channel: server messages don't carry a per-message channel (yet), so
//   we render every persisted message as `in_app`. Future server work will
//   tag messages with email/sms/whatsapp.

import { ApiError } from "@tkos/api-client";
import { confirmDialog, iconMarkup } from "@tkos/shared-ui";
import { openLightbox, type LightboxImage } from "../../shell/lightbox";
import type { CommChannel } from "@tkos/tkos-types";

import { registerPage } from "../../shell/router";
import * as realtime from "../../shell/realtime";
import { attachAutoGrow, resetAutoGrow } from "../../shell/auto-grow";
import {
  ATTACH_ACCEPT_ATTR,
  attachmentTrayHtml,
  uploadMessageAttachments,
  type MessageAttachment as SharedAttachment,
} from "../../shell/message-attachments";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

type TabKey = "customers" | "suppliers" | "team";

/** Per-message channel badge — defaults to in_app for stored messages. */
/** A file attached to a message — minted by the upload endpoint, then
 *  passed back as `attachmentIds` when the parent message is sent. */
interface MessageAttachment {
  id: string;
  url: string;
  name: string;
  mimeType: string;
  size: number;
  kind: "image" | "pdf" | "other";
}

interface ThreadMessage {
  id: string;
  threadId: string;
  from: "me" | "them";
  text: string;
  ts: number;
  channel: CommChannel;
  attachments?: MessageAttachment[];
  /** When the customer first read this (outbound only) → "Seen" indicator. */
  readAt?: number | null;
  /** Optimistic-send placeholder until server confirms. */
  pending?: boolean;
  /** Server save failed — show retry hint. */
  failed?: boolean;
}

interface Thread {
  id: string;
  tab: TabKey;
  contactId: string;
  contactName: string;
  /** Customer's real email (customer threads) — needed so a reply can resolve
   *  their phone for the outbound SMS notification and key the right thread. */
  contactEmail: string;
  contactColor: string;
  /** Subject for the thread (master doc: "Threaded conversations with subject lines"). */
  subject: string;
  messages: ThreadMessage[];
  unread: number;
  lastTs: number;
}

/** Loose shape we accept from /api/portal/messages. */
interface ServerMessage {
  id?: string;
  threadId?: string;
  direction?: string;
  body?: string;
  text?: string;
  subject?: string;
  channel?: string;
  createdAt?: string | number;
  ts?: string | number;
  from?: string;
  readAt?: number | string | null;
  unread?: boolean;
  retailerName?: string;
  customerName?: string;
  supplierName?: string;
  retailerId?: string;
  supplierId?: string;
  customerId?: string;
  attachments?: unknown;
}

interface ServerThread {
  threadId?: string;
  /** Supplier/retailer thread builders emit the thread key as `id`, not `threadId`. */
  id?: string;
  subject?: string;
  lastSubject?: string;
  retailerName?: string;
  customerName?: string;
  supplierName?: string;
  /** Display name of the external party (the customer) on supplierThreads. */
  retailer?: string;
  retailerEmail?: string;
  retailerId?: string;
  supplierId?: string;
  customerId?: string;
  unreadForRetailer?: number;
  /** Per-thread unread count emitted by the thread builders. */
  unread?: number;
  /** "supplier" if returned via supplierThreads (i.e. addressed to retailer-as-supplier). */
  pov?: string;
  messages?: ServerMessage[];
  /** Some endpoints add this hint. */
  kind?: string;
}

interface MessagesListResponse {
  ok?: boolean;
  messages?: ServerMessage[];
  threads?: ServerThread[];
  supplierThreads?: ServerThread[];
}

interface SendMessageResponse {
  ok?: boolean;
  message?: ServerMessage;
  error?: string;
}

// ── Channel handling (mirrors crm page) ─────────────────────────────────────

const CHANNEL_LABELS: Record<CommChannel, string> = {
  email: "Email",
  whatsapp: "WhatsApp",
  phone: "Phone",
  sms: "SMS",
  in_app: "In-app",
};

const CHANNEL_COLORS: Record<CommChannel, string> = {
  email: "#3b82f6",
  whatsapp: "#22c55e",
  phone: "#a855f7",
  sms: "#0ea5e9",
  in_app: "#e58200",
};

function coerceChannel(raw: unknown): CommChannel {
  if (typeof raw !== "string") return "in_app";
  const lc = raw.trim().toLowerCase().replace(/[\s-]/g, "_");
  if (lc === "email" || lc === "whatsapp" || lc === "phone" || lc === "sms" || lc === "in_app") {
    return lc;
  }
  if (lc === "inapp") return "in_app";
  return "in_app";
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const RTF = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });
const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
});

function relTime(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return "—";
  const diffSec = Math.round((ms - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return RTF.format(diffSec, "second");
  if (abs < 3600) return RTF.format(Math.round(diffSec / 60), "minute");
  if (abs < 86400) return RTF.format(Math.round(diffSec / 3600), "hour");
  if (abs < 86400 * 7) return RTF.format(Math.round(diffSec / 86400), "day");
  return DATE_FMT.format(new Date(ms));
}

// Compact stamp for the "Seen" indicator, e.g. "26 Jun 15:14".
const SEEN_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
});
function fmtSeenStamp(ms: number): string {
  return SEEN_FMT.format(new Date(ms)).replace(",", "");
}

function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?"
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

// MIME allowlist + size limit live in shell/message-attachments.ts so the
// thread reply, the compose modal and the customer-record compose popup
// all share one source of truth.

function kindOf(mime: string): MessageAttachment["kind"] {
  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf") return "pdf";
  return "other";
}

function normaliseAttachments(raw: unknown): MessageAttachment[] {
  if (!Array.isArray(raw)) return [];
  const out: MessageAttachment[] = [];
  for (const a of raw) {
    if (!a || typeof a !== "object") continue;
    const r = a as Record<string, unknown>;
    const id   = typeof r.id   === "string" ? r.id   : "";
    const url  = typeof r.url  === "string" ? r.url  : "";
    const name = typeof r.name === "string" ? r.name : "";
    const mime = typeof r.mimeType === "string" ? r.mimeType : "";
    const size = typeof r.size === "number" ? r.size : 0;
    if (!id || !url) continue;
    out.push({ id, url, name: name || id, mimeType: mime, size, kind: kindOf(mime) });
  }
  return out;
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Render an attachment list for a bubble. Images render inline (small
 *  square thumb that clicks through to full size); PDFs / other render
 *  as a download row with filename + size. */
function renderAttachments(items: MessageAttachment[] | undefined): string {
  if (!items || items.length === 0) return "";
  return `<div class="msg-bubble__attachments">${items
    .map((a) => {
      if (a.kind === "image") {
        return `
          <a class="msg-attach msg-attach--image"
             href="${escapeHtml(a.url)}"
             target="_blank" rel="noopener noreferrer"
             aria-label="${escapeHtml(a.name)}">
            <img src="${escapeHtml(a.url)}" alt="${escapeHtml(a.name)}" loading="lazy" />
          </a>
        `;
      }
      const sizeLabel = formatBytes(a.size);
      return `
        <a class="msg-attach msg-attach--file"
           href="${escapeHtml(a.url)}"
           target="_blank" rel="noopener noreferrer"
           download="${escapeHtml(a.name)}">
          <span class="msg-attach__icon" aria-hidden="true">📄</span>
          <span class="msg-attach__meta">
            <span class="msg-attach__name">${escapeHtml(a.name)}</span>
            ${sizeLabel ? `<span class="msg-attach__size">${escapeHtml(sizeLabel)}</span>` : ""}
          </span>
        </a>
      `;
    })
    .join("")}</div>`;
}

/** Thin wrapper around the shared shell helper. Kept in this file so call
 *  sites don't churn — the implementation lives in shell/message-attachments
 *  so the customer-compose modal (and any other compose surface) shares the
 *  same upload contract. */
async function uploadAttachments(files: File[]): Promise<MessageAttachment[]> {
  return uploadMessageAttachments(files) as Promise<SharedAttachment[]> as Promise<MessageAttachment[]>;
}

function safeId(): string {
  return `tmp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function colorFromString(s: string): string {
  // Stable but cheap hash → hue.
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  const palette = ["#e58200", "#3b82f6", "#a855f7", "#22c55e", "#0ea5e9", "#ef4444", "#f59e0b"];
  return palette[Math.abs(h) % palette.length] ?? "#e58200";
}

function coerceTs(raw: unknown): number {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    const t = Date.parse(raw);
    if (Number.isFinite(t)) return t;
  }
  return Date.now();
}

// ── Server → in-memory normalisation ────────────────────────────────────────

function classifyTab(t: ServerThread): TabKey {
  // POV hint from the server: when threads come back via `supplierThreads`,
  // the retailer is acting as the supplier — i.e. the other party is a
  // customer/end-user. Treat those as "customers".
  if (t.pov === "supplier") return "customers";
  // Heuristic: if the thread carries a supplierId that does not match this
  // retailer's own id, the retailer is talking *to* a supplier.
  if (t.supplierId && !t.customerId) return "suppliers";
  if (t.customerId || t.customerName) return "customers";
  // Fallback: assume supplier (most legacy threads were supplier→retailer).
  return "suppliers";
}

function normaliseThread(t: ServerThread, ownRetailerId: string): Thread | null {
  // The server thread builders emit the key as `id`; older shapes use
  // `threadId`. Accept either, else this thread (and every customer thread)
  // would be silently dropped.
  const tid = t.threadId ?? t.id;
  if (!tid) return null;
  const tab = classifyTab(t);
  const contactName =
    (tab === "customers"
      ? t.customerName ?? t.retailer ?? t.retailerName ?? t.retailerEmail
      : t.supplierName) ||
    t.retailer ||
    t.retailerName ||
    t.retailerEmail ||
    "Unknown";
  const subject = (t.subject ?? t.lastSubject ?? "General Enquiry").trim() || "General Enquiry";
  const contactId = String(t.customerId ?? t.supplierId ?? t.retailerId ?? contactName);

  const messages: ThreadMessage[] = (t.messages ?? []).map((m, i) => {
    const ts = coerceTs(m.createdAt ?? m.ts);
    // direction "retailer-to-supplier" = we sent it (from the retailer POV);
    // anything else came inbound. When the thread is from supplierThreads
    // (pov=supplier), the retailer is the recipient end, so the polarity
    // flips.
    const dir = String(m.direction ?? "").toLowerCase();
    let from: "me" | "them";
    if (tab === "customers" && t.pov === "supplier") {
      // Retailer-as-supplier POV: outgoing from retailer = supplier-to-retailer.
      from = dir === "supplier-to-retailer" ? "me" : "them";
    } else {
      from = dir === "retailer-to-supplier" || m.from === "me" ? "me" : "them";
    }
    return {
      id: String(m.id ?? `${tid}-${i}`),
      threadId: String(tid),
      from,
      text: String(m.body ?? m.text ?? "").trim(),
      ts,
      channel: coerceChannel(m.channel),
      attachments: normaliseAttachments(m.attachments),
      readAt: m.readAt == null ? null : coerceTs(m.readAt),
    };
  });

  messages.sort((a, b) => a.ts - b.ts);
  const lastTs = messages.length > 0 ? messages[messages.length - 1]!.ts : 0;

  // ownRetailerId is reserved for future "team" attribution; not used for
  // customer/supplier classification (tab heuristic above already covers it).
  void ownRetailerId;

  return {
    id: String(tid),
    tab,
    contactId,
    contactName,
    contactEmail: String(t.retailerEmail ?? "").trim(),
    contactColor: colorFromString(contactName),
    subject,
    messages,
    unread:
      typeof t.unreadForRetailer === "number"
        ? t.unreadForRetailer
        : typeof t.unread === "number"
          ? t.unread
          : 0,
    lastTs,
  };
}

// ── Backend ──────────────────────────────────────────────────────────────────

async function fetchThreads(
  ctx: PageContext,
  ownRetailerId: string,
  signal: AbortSignal,
): Promise<Thread[]> {
  const params = new URLSearchParams();
  if (ownRetailerId) {
    params.set("retailerId", ownRetailerId);
    // Customer↔retailer threads store the retailer in the `supplierId` slot
    // (retailer-as-supplier flow), so they only come back via `supplierThreads`
    // when supplierId is supplied. Without this the Messages page never showed
    // any customer conversations.
    params.set("supplierId", ownRetailerId);
  }
  const qs = params.toString();
  const path = qs ? `/portal/messages?${qs}` : `/portal/messages`;

  const resp = await ctx.api<MessagesListResponse>(path, { signal });
  const out: Thread[] = [];
  for (const t of resp.threads ?? []) {
    const norm = normaliseThread({ ...t, pov: t.pov ?? "retailer" }, ownRetailerId);
    if (norm) out.push(norm);
  }
  for (const t of resp.supplierThreads ?? []) {
    const norm = normaliseThread({ ...t, pov: "supplier" }, ownRetailerId);
    if (norm) out.push(norm);
  }
  // Dedupe by id (server can occasionally return the same thread twice).
  const seen = new Set<string>();
  return out.filter((t) => {
    if (seen.has(t.id)) return false;
    seen.add(t.id);
    return true;
  });
}

interface ReplyArgs {
  thread: Thread;
  body: string;
  ownRetailerId: string;
  retailerName: string;
  attachments?: MessageAttachment[];
}

async function postReply(
  ctx: PageContext,
  args: ReplyArgs,
  signal: AbortSignal,
): Promise<ServerMessage | null> {
  const { thread, body, ownRetailerId, retailerName, attachments } = args;
  const attachmentIds = (attachments ?? []).map((a) => a.id);
  if (thread.tab === "suppliers") {
    const resp = await ctx.api<SendMessageResponse>("/portal/messages", {
      method: "POST",
      signal,
      body: JSON.stringify({
        supplierId: thread.contactId,
        subject: thread.subject,
        body,
        retailerId: ownRetailerId,
        retailerName,
        inReplyTo: thread.id,
        attachmentIds,
      }),
    });
    return resp.message ?? null;
  }
  if (thread.tab === "customers") {
    const resp = await ctx.api<SendMessageResponse>("/portal/customer-message", {
      method: "POST",
      signal,
      body: JSON.stringify({
        retailerId: ownRetailerId,
        retailerName,
        customerId: thread.contactId,
        // Pass the real email so the server can resolve the customer's phone
        // (for the SMS notification) and key the reply to the right thread.
        customerEmail: thread.contactEmail,
        customerName: thread.contactName,
        subject: thread.subject,
        body,
        attachmentIds,
      }),
    });
    return resp.message ?? null;
  }
  // Team tab — no backend route. Surface to the caller as a "stub" success
  // so the optimistic bubble stays.
  return null;
}

interface ComposeArgs {
  tab: TabKey;
  recipient: ComposeContact;
  subject: string;
  body: string;
  ownRetailerId: string;
  retailerName: string;
  attachmentIds?: string[];
}

async function postCompose(
  ctx: PageContext,
  args: ComposeArgs,
  signal: AbortSignal,
): Promise<ServerMessage | null> {
  const attachmentIds = args.attachmentIds ?? [];
  if (args.tab === "suppliers") {
    const resp = await ctx.api<SendMessageResponse>("/portal/messages", {
      method: "POST",
      signal,
      body: JSON.stringify({
        supplierId: args.recipient.id,
        subject: args.subject,
        body: args.body,
        retailerId: args.ownRetailerId,
        retailerName: args.retailerName,
        attachmentIds,
      }),
    });
    return resp.message ?? null;
  }
  if (args.tab === "customers") {
    const resp = await ctx.api<SendMessageResponse>("/portal/customer-message", {
      method: "POST",
      signal,
      body: JSON.stringify({
        retailerId: args.ownRetailerId,
        retailerName: args.retailerName,
        customerId: args.recipient.id,
        customerName: args.recipient.name,
        customerEmail: args.recipient.email,
        subject: args.subject,
        body: args.body,
        attachmentIds,
      }),
    });
    return resp.message ?? null;
  }
  // Team — no endpoint yet; the optimistic flow handles the bubble.
  return null;
}

async function deleteThread(
  ctx: PageContext,
  threadId: string,
  signal: AbortSignal,
): Promise<boolean> {
  try {
    await ctx.api(`/portal/messages/thread/${encodeURIComponent(threadId)}`, {
      method: "DELETE",
      signal,
    });
    return true;
  } catch (err) {
    if (isAbortError(err) || signal.aborted) return false;
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) return true;
    throw err;
  }
}

async function markRead(
  ctx: PageContext,
  threadId: string,
  role: "supplier" | "retailer",
  signal: AbortSignal,
): Promise<void> {
  try {
    await ctx.api("/portal/messages/mark-read", {
      method: "POST",
      signal,
      // `role` = the side the RETAILER plays in this thread: 'supplier' for
      // customer conversations, 'retailer' for the retailer's own supplier
      // conversations. Sending the wrong role leaves the unread dot to
      // resurrect on the next poll.
      body: JSON.stringify({ threadId, role }),
    });
  } catch (err) {
    if (isAbortError(err) || signal.aborted) return;
    // Best-effort — not user-visible.
    console.warn("[messages] mark-read failed:", err);
  }
}

// ── Rendering helpers ───────────────────────────────────────────────────────

function shellHtml(): string {
  return `
    <section class="msg-root" aria-label="Messages">
      <header class="msg-header">
        <div class="msg-header__title">
          <span class="msg-header__icon" aria-hidden="true">
            ${iconMarkup("chat", { size: "md" })}
          </span>
          <div>
            <h1>Messages</h1>
            <p>Threaded conversations with subject lines</p>
          </div>
        </div>
        <div class="msg-header__tools">
          <label class="msg-search">
            ${iconMarkup("search", { size: "sm" })}
            <input type="search" placeholder="Search threads…" data-role="search" />
          </label>
          <button type="button" class="msg-btn msg-btn--primary" data-action="compose">+ Compose</button>
        </div>
      </header>

      <div class="msg-banner" data-role="banner" hidden></div>

      <nav class="msg-tabs" role="tablist" data-role="tabs">
        <button type="button" role="tab" class="msg-tab" data-tab="customers" aria-selected="true">
          Customers <span class="msg-tab__count" data-count="customers">0</span>
        </button>
        <button type="button" role="tab" class="msg-tab" data-tab="suppliers" aria-selected="false">
          Suppliers <span class="msg-tab__count" data-count="suppliers">0</span>
        </button>
        <button type="button" role="tab" class="msg-tab" data-tab="team" aria-selected="false">
          Team <span class="msg-tab__count" data-count="team">0</span>
        </button>
      </nav>

      <div class="msg-split" data-role="split">
        <aside class="msg-list" aria-label="Threads">
          <div class="msg-list__header" data-role="list-header">Threads</div>
          <ul class="msg-list__rows" data-role="list-rows" role="list"></ul>
        </aside>
        <section class="msg-thread" aria-label="Conversation" data-role="thread">
          <div class="msg-thread__empty">Select a conversation to read.</div>
        </section>
      </div>
    </section>
  `;
}

function renderThreadList(
  host: HTMLElement,
  threads: Thread[],
  activeId: string | null,
): void {
  if (threads.length === 0) {
    host.innerHTML = `<li class="msg-list__empty">No threads yet.</li>`;
    return;
  }
  host.innerHTML = threads
    .map((t) => {
      const last = t.messages[t.messages.length - 1];
      const preview = last ? last.text.slice(0, 60) + (last.text.length > 60 ? "…" : "") : "";
      const isActive = t.id === activeId;
      return `
        <li class="msg-row${isActive ? " is-active" : ""}${t.unread > 0 ? " is-unread" : ""}"
            data-thread-id="${escapeHtml(t.id)}"
            role="button" tabindex="0"
            aria-label="Open thread with ${escapeHtml(t.contactName)} — ${escapeHtml(t.subject)}">
          <span class="msg-row__avatar" aria-hidden="true"
                style="background:${escapeHtml(t.contactColor)}">${escapeHtml(initialsOf(t.contactName))}</span>
          <div class="msg-row__main">
            <div class="msg-row__top">
              <span class="msg-row__name"${t.tab === "customers" ? ` data-customer-name="${escapeHtml(t.contactName)}" data-customer-id="${escapeHtml(t.contactId)}" style="cursor:pointer"` : ""}>${escapeHtml(t.contactName)}</span>
              <span class="msg-row__time">${escapeHtml(relTime(t.lastTs))}</span>
            </div>
            <div class="msg-row__subject">${escapeHtml(t.subject)}</div>
            <div class="msg-row__preview">${escapeHtml(preview)}</div>
          </div>
          ${t.unread > 0 ? `<span class="msg-row__dot" aria-label="${t.unread} unread"></span>` : ""}
        </li>
      `;
    })
    .join("");
}

function renderActiveThread(host: HTMLElement, thread: Thread | null): void {
  if (!thread) {
    host.innerHTML = `<div class="msg-thread__empty">Select a conversation to read.</div>`;
    return;
  }
  const bubbles = thread.messages
    .map((m) => {
      const side = m.from === "me" ? "me" : "them";
      const channelColor = CHANNEL_COLORS[m.channel];
      const channelLabel = CHANNEL_LABELS[m.channel];
      const status = m.pending ? "Sending…" : m.failed ? "Failed" : relTime(m.ts);
      // "Seen" on outbound messages the customer has read (mirrors quote Seen).
      const seenHtml = (side === "me" && typeof m.readAt === "number" && m.readAt > 0)
        ? `<span class="msg-bubble__seen" title="Customer read this message">Seen ${escapeHtml(fmtSeenStamp(m.readAt))}</span>`
        : "";
      // Empty bubble text is fine when the message is image-only — don't
      // render an empty text node in that case so spacing is tight.
      const textHtml = m.text
        ? `<div class="msg-bubble__text">${escapeHtml(m.text)}</div>`
        : "";
      return `
        <article class="msg-bubble msg-bubble--${side}${m.pending ? " is-pending" : ""}${m.failed ? " is-failed" : ""}">
          ${textHtml}
          ${renderAttachments(m.attachments)}
          <div class="msg-bubble__meta">
            <span class="msg-bubble__channel" style="--msg-channel-color:${channelColor}">${escapeHtml(channelLabel)}</span>
            <span class="msg-bubble__time">${escapeHtml(status)}</span>
            ${seenHtml}
          </div>
        </article>
      `;
    })
    .join("");

  // Quick replies are offered only on customer threads when it's the retailer's
  // turn (i.e. the last message came from the customer). Kit drafts three
  // options on demand — see openQuickReplies / renderQuickReplies in mount().
  const lastMsg = thread.messages[thread.messages.length - 1];
  const showQuickReplies = thread.tab === "customers" && lastMsg?.from === "them";

  host.innerHTML = `
    <header class="msg-thread__head">
      <span class="msg-thread__avatar" aria-hidden="true"
            style="background:${escapeHtml(thread.contactColor)}">${escapeHtml(initialsOf(thread.contactName))}</span>
      <div class="msg-thread__heading">
        <div class="msg-thread__name"${thread.tab === "customers" ? ` data-customer-name="${escapeHtml(thread.contactName)}" data-customer-id="${escapeHtml(thread.contactId)}" style="cursor:pointer"` : ""}>${escapeHtml(thread.contactName)}</div>
        <div class="msg-thread__subject">${escapeHtml(thread.subject)}</div>
      </div>
      <div class="msg-thread__actions">
        <button type="button" class="msg-btn msg-btn--mobile-back" data-action="back">‹ Back</button>
        <button type="button" class="msg-btn msg-btn--danger" data-action="delete-thread">Delete Thread</button>
      </div>
    </header>
    <div class="msg-thread__body" data-role="bubbles">
      ${bubbles || `<div class="msg-thread__empty-body">No messages yet.</div>`}
    </div>
    ${thread.tab === "customers" ? `<div class="msg-pinned-notes" data-role="pinned-notes" hidden></div>` : ""}
    ${showQuickReplies ? `<div class="msg-quick-replies" data-role="quick-replies" hidden></div>` : ""}
    <form class="msg-thread__foot" data-role="reply-form">
      <button type="button" class="msg-attach-btn" data-action="attach" aria-label="Attach file">
        ${iconMarkup("paperclip", { size: "sm" })}
      </button>
      <input type="file" class="msg-attach-input" data-role="attach-input"
             accept="image/jpeg,image/png,image/webp,application/pdf"
             multiple hidden />
      <button type="button" class="msg-templates-btn" data-action="templates" aria-label="Insert a message template" title="Message templates">
        ${iconMarkup("chat", { size: "sm" })}
      </button>
      ${showQuickReplies ? `<button type="button" class="msg-quick-btn" data-action="suggest-replies" aria-label="Suggest replies with Kit" title="Suggest replies ✨">
        ${iconMarkup("sparkle", { size: "sm" })}
      </button>` : ""}
      <textarea class="msg-reply" data-role="reply" rows="1"
                placeholder="Reply to ${escapeHtml(thread.contactName.split(" ")[0] ?? thread.contactName)}…"></textarea>
      <button type="submit" class="msg-send" aria-label="Send reply">
        ${iconMarkup("send", { size: "sm" })}
      </button>
      <div class="msg-attach-tray" data-role="attach-tray" hidden></div>
    </form>
  `;
  // Auto-scroll to bottom.
  const bubblesEl = host.querySelector<HTMLElement>("[data-role='bubbles']");
  if (bubblesEl) bubblesEl.scrollTop = bubblesEl.scrollHeight;
}

// ── Compose modal ───────────────────────────────────────────────────────────

/** A single recipient option in the compose picker — unified across the
 *  three tabs (customer / supplier / team). The `id` is whatever the
 *  send endpoint needs (customer uuid, supplier uuid, team userId).  */
interface ComposeContact {
  id: string;
  name: string;
  email: string;
}

interface ComposeRefs {
  dialog: HTMLDialogElement;
  form: HTMLFormElement;
  /** Search input inside the picker; visible while no contact is selected. */
  pickerSearch: HTMLInputElement;
  /** Drop-down listbox that appears as the user types. */
  pickerResults: HTMLDivElement;
  /** "Selected" pill that replaces the search input once a contact is chosen. */
  pickerSelected: HTMLDivElement;
  /** Inner chip text (the chosen contact's name). */
  pickerChip: HTMLSpanElement;
  /** "Change" button on the selected pill — re-opens the search input. */
  pickerClear: HTMLButtonElement;
  /** Wrapper around the search input — hidden once a contact is picked. */
  pickerSearchWrap: HTMLDivElement;
  /** Small "Loading…" / "No matches" status line below the search box. */
  pickerHint: HTMLDivElement;
  /** Header label above the picker (changes per tab). */
  recipientLabel: HTMLElement;
  subject: HTMLInputElement;
  /** Subject field wrapper — hidden for customer messages (subject only
   *  applies to supplier + team messages). */
  subjectField: HTMLElement;
  body: HTMLTextAreaElement;
  tabSelect: HTMLSelectElement;
  cancelBtn: HTMLButtonElement;
  /** Paperclip button — opens the hidden file input. */
  attachBtn: HTMLButtonElement;
  /** Hidden file input wired to onAttachInputChange. */
  attachInput: HTMLInputElement;
  /** Tray that renders queued attachment chips. */
  attachTray: HTMLDivElement;
  /** Drop zone wrapping the paperclip + hint. */
  dropZone: HTMLDivElement;
}

function buildComposeModal(): ComposeRefs {
  const dialog = document.createElement("dialog");
  dialog.className = "msg-modal";
  dialog.innerHTML = `
    <form class="msg-modal__form" data-role="compose-form" method="dialog">
      <header class="msg-modal__head">
        <h2>New message</h2>
        <button type="button" class="msg-modal__close" data-action="close" aria-label="Close">&times;</button>
      </header>
      <div class="msg-modal__body">
        <label class="msg-field">
          <span>To</span>
          <select data-role="compose-tab">
            <option value="customers">Customer</option>
            <option value="suppliers">Supplier</option>
            <option value="team">Team member</option>
          </select>
        </label>
        <div class="msg-field msg-picker" data-role="compose-picker">
          <span data-role="compose-recipient-label">Recipient</span>
          <div class="msg-picker__search-wrap" data-role="compose-picker-search-wrap">
            <input type="text"
                   class="msg-picker__search"
                   data-role="compose-picker-search"
                   autocomplete="off"
                   role="combobox"
                   aria-autocomplete="list"
                   aria-expanded="false"
                   placeholder="Search by name…" />
            <div class="msg-picker__results"
                 role="listbox"
                 data-role="compose-picker-results"
                 hidden></div>
            <div class="msg-picker__hint" data-role="compose-picker-hint"></div>
          </div>
          <div class="msg-picker__selected" data-role="compose-picker-selected" hidden>
            <span class="msg-picker__chip" data-role="compose-picker-chip"></span>
            <button type="button" class="msg-picker__clear" data-role="compose-picker-clear">Change</button>
          </div>
        </div>
        <label class="msg-field" data-role="compose-subject-field">
          <span>Subject</span>
          <input type="text" maxlength="200" data-role="compose-subject" />
        </label>
        <label class="msg-field">
          <span>Message</span>
          <textarea required rows="5" maxlength="8000" data-role="compose-body"></textarea>
        </label>
        <div class="msg-field msg-compose-attach" data-role="compose-drop-zone">
          <button type="button" class="msg-attach-btn"
                  data-action="compose-attach" aria-label="Attach file">
            ${iconMarkup("paperclip", { size: "sm" })}
            <span>Attach files</span>
          </button>
          <span class="msg-compose-attach-hint">
            Drag &amp; drop or click — JPG, PNG, WebP, PDF · max 10 MB each
          </span>
          <input type="file" class="msg-attach-input"
                 data-role="compose-attach-input"
                 accept="${ATTACH_ACCEPT_ATTR}" multiple hidden />
        </div>
        <div class="msg-attach-tray" data-role="compose-attach-tray" hidden></div>
      </div>
      <footer class="msg-modal__foot">
        <button type="button" class="msg-btn" data-action="cancel">Cancel</button>
        <button type="submit" class="msg-btn msg-btn--primary">Send</button>
      </footer>
    </form>
  `;
  return {
    dialog,
    form: dialog.querySelector<HTMLFormElement>("[data-role='compose-form']")!,
    pickerSearch: dialog.querySelector<HTMLInputElement>("[data-role='compose-picker-search']")!,
    pickerResults: dialog.querySelector<HTMLDivElement>("[data-role='compose-picker-results']")!,
    pickerSelected: dialog.querySelector<HTMLDivElement>("[data-role='compose-picker-selected']")!,
    pickerChip: dialog.querySelector<HTMLSpanElement>("[data-role='compose-picker-chip']")!,
    pickerClear: dialog.querySelector<HTMLButtonElement>("[data-role='compose-picker-clear']")!,
    pickerSearchWrap: dialog.querySelector<HTMLDivElement>("[data-role='compose-picker-search-wrap']")!,
    pickerHint: dialog.querySelector<HTMLDivElement>("[data-role='compose-picker-hint']")!,
    recipientLabel: dialog.querySelector<HTMLElement>("[data-role='compose-recipient-label']")!,
    subject: dialog.querySelector<HTMLInputElement>("[data-role='compose-subject']")!,
    subjectField: dialog.querySelector<HTMLElement>("[data-role='compose-subject-field']")!,
    body: dialog.querySelector<HTMLTextAreaElement>("[data-role='compose-body']")!,
    tabSelect: dialog.querySelector<HTMLSelectElement>("[data-role='compose-tab']")!,
    cancelBtn: dialog.querySelector<HTMLButtonElement>("[data-action='cancel']")!,
    attachBtn: dialog.querySelector<HTMLButtonElement>("[data-action='compose-attach']")!,
    attachInput: dialog.querySelector<HTMLInputElement>("[data-role='compose-attach-input']")!,
    attachTray: dialog.querySelector<HTMLDivElement>("[data-role='compose-attach-tray']")!,
    dropZone: dialog.querySelector<HTMLDivElement>("[data-role='compose-drop-zone']")!,
  };
}

// ── Page ─────────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 3000;

const messagesPage: Page = {
  name: "messages",
  label: "Messages",
  icon: "",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();
    let disposed = false;

    let allThreads: Thread[] = [];
    let activeTab: TabKey = "customers";
    let activeThreadId: string | null = null;
    // Thread whose pane is currently painted — lets rerender() tell a
    // background refresh (WS message.received / 30s poll) of the SAME
    // conversation apart from a deliberate thread/tab switch, so the reply
    // draft is preserved on the former but reset on the latter.
    let lastRenderedThreadId: string | null = null;
    let searchQuery = "";
    // Attachments the user has picked but not yet sent, per thread.
    // Survives re-renders of the thread pane so the tray persists while
    // they type the message body.
    const pendingAttachments = new Map<string, MessageAttachment[]>();

    // Quick-reply suggestions (Kit) — cached per inbound message so reopening a
    // thread, or the 3s poll re-rendering it, doesn't re-hit the model. Key is
    // `${threadId}:${lastInboundTs}`, so a NEW customer message invalidates the
    // cache and the retailer can ask Kit again. `quickRepliesFor` is the thread
    // whose chips are currently expanded (null = collapsed everywhere).
    const quickReplyCache = new Map<string, string[]>();
    let quickRepliesFor: string | null = null;
    let quickReplyLoading = false;

    // Pinned notes (read-only here) — the always-visible customer-handling
    // flags from the customer record, shown above the composer so the retailer
    // sees e.g. "always email" while writing. Edited on the record, not here.
    // Cached per customer id; fetched once when a customer thread is opened.
    const pinnedNotesCache = new Map<string, string[]>();

    // Cross-page focus hint: when the Quote Builder header's message
    // quick-action navigates here, it passes `{contact: "<Customer Name>"}`
    // so we can open straight to the right thread. Honoured exactly once,
    // after the first successful loadThreads() pass.
    let pendingContactFocus: string | null = ctx.params["contact"] ?? null;
    // When a competitor alert's "Draft a reply" navigates here, auto-open Kit's
    // (competitor-aware) quick replies on the focused thread, once.
    let pendingSuggest = ctx.params["suggest"] != null;

    const ownRetailerId =
      // The legacy portal stores this in localStorage; new portal reads
      // retailerId from the authenticated user (see shell/auth.ts).
      (typeof localStorage !== "undefined" ? localStorage.getItem("kos_retailer_id") : null) ??
      ctx.user?.retailerId ??
      "";
    const retailerName = ctx.user?.retailerName ?? "Retailer";

    container.innerHTML = shellHtml();
    const tabsHost = container.querySelector<HTMLElement>("[data-role='tabs']")!;
    const listHeader = container.querySelector<HTMLElement>("[data-role='list-header']")!;
    const listRows = container.querySelector<HTMLElement>("[data-role='list-rows']")!;
    const threadHost = container.querySelector<HTMLElement>("[data-role='thread']")!;
    const splitHost = container.querySelector<HTMLElement>("[data-role='split']")!;
    const searchInput = container.querySelector<HTMLInputElement>("[data-role='search']")!;
    const bannerEl = container.querySelector<HTMLElement>("[data-role='banner']")!;
    const composeBtn = container.querySelector<HTMLButtonElement>("[data-action='compose']")!;

    // Compose modal — appended to root so backdrop covers everything.
    const compose = buildComposeModal();
    container.appendChild(compose.dialog);
    // Grow the compose body with the message (cap a touch higher than the
    // inline reply since this is the full-compose surface).
    attachAutoGrow(compose.body, 280);

    function setBanner(msg: string | null): void {
      if (!msg) {
        bannerEl.hidden = true;
        bannerEl.textContent = "";
        return;
      }
      bannerEl.hidden = false;
      bannerEl.textContent = msg;
    }

    function visibleThreads(): Thread[] {
      const q = searchQuery.trim().toLowerCase();
      let list = allThreads.filter((t) => t.tab === activeTab);
      if (q) {
        list = list.filter((t) => {
          if (t.contactName.toLowerCase().includes(q)) return true;
          if (t.subject.toLowerCase().includes(q)) return true;
          return t.messages.some((m) => m.text.toLowerCase().includes(q));
        });
      }
      list.sort((a, b) => b.lastTs - a.lastTs);
      return list;
    }

    function updateTabCounts(): void {
      for (const tab of ["customers", "suppliers", "team"] as const) {
        const el = tabsHost.querySelector<HTMLElement>(`[data-count='${tab}']`);
        if (!el) continue;
        const n = allThreads.filter((t) => t.tab === tab).length;
        el.textContent = String(n);
      }
    }

    function syncTabSelection(): void {
      tabsHost.querySelectorAll<HTMLButtonElement>(".msg-tab").forEach((btn) => {
        const sel = btn.dataset.tab === activeTab;
        btn.setAttribute("aria-selected", sel ? "true" : "false");
        btn.classList.toggle("is-active", sel);
      });
    }

    function rerender(): void {
      const threads = visibleThreads();
      // If the active thread is no longer visible, drop the selection.
      if (activeThreadId && !threads.some((t) => t.id === activeThreadId)) {
        activeThreadId = null;
      }
      const active = threads.find((t) => t.id === activeThreadId) ?? null;
      const labels: Record<TabKey, string> = {
        customers: "Customer Threads",
        suppliers: "Supplier Threads",
        team: "Team Threads",
      };
      listHeader.textContent = `${labels[activeTab]} (${threads.length})`;
      renderThreadList(listRows, threads, activeThreadId);

      // renderActiveThread() rebuilds the pane via innerHTML, destroying the
      // reply <textarea>. When this is a background refresh of the SAME thread
      // (live WS message / poll), capture the in-progress draft + caret + focus
      // first and restore it onto the freshly-rendered textarea — otherwise a
      // retailer mid-sentence loses what they typed. A real thread switch
      // (different id) skips this, giving a clean compose box.
      const sameThread = active != null && active.id === lastRenderedThreadId;
      let draft: { value: string; start: number; end: number; focused: boolean } | null = null;
      // Where the reader was in the conversation, captured before the rebuild.
      // renderActiveThread() always jumps to the bottom, which is right for a
      // freshly-opened thread and wrong for a background refresh: someone
      // scrolled up reading older messages got yanked back down every 3s.
      let scroll: { top: number; atBottom: boolean } | null = null;
      if (sameThread) {
        const prev = threadHost.querySelector<HTMLTextAreaElement>("[data-role='reply']");
        if (prev) {
          draft = {
            value: prev.value,
            start: prev.selectionStart ?? prev.value.length,
            end: prev.selectionEnd ?? prev.value.length,
            focused: document.activeElement === prev,
          };
        }
        const prevBubbles = threadHost.querySelector<HTMLElement>("[data-role='bubbles']");
        if (prevBubbles) {
          // "Near enough" the bottom counts as at the bottom, so a new message
          // still follows you down when you're reading the live end of the
          // thread. One bubble's worth of slack.
          const distanceFromBottom =
            prevBubbles.scrollHeight - prevBubbles.scrollTop - prevBubbles.clientHeight;
          scroll = { top: prevBubbles.scrollTop, atBottom: distanceFromBottom <= 64 };
        }
      }

      renderActiveThread(threadHost, active);

      // Put the reader back where they were. Only a refresh of the same thread
      // restores; opening a different thread keeps the jump-to-bottom.
      if (scroll && !scroll.atBottom) {
        const nextBubbles = threadHost.querySelector<HTMLElement>("[data-role='bubbles']");
        if (nextBubbles) nextBubbles.scrollTop = scroll.top;
      }

      // Restore focus whenever the box HAD focus — not only when something was
      // typed. Clicking the empty reply box and pausing used to lose the caret
      // on the next poll, so the click appeared not to register and you had to
      // click again.
      if (draft && (draft.value || draft.focused)) {
        const next = threadHost.querySelector<HTMLTextAreaElement>("[data-role='reply']");
        if (next) {
          if (draft.value) next.value = draft.value;
          if (draft.focused) {
            next.focus();
            try { next.setSelectionRange(draft.start, draft.end); } catch { /* non-text input edge */ }
          }
        }
      }
      lastRenderedThreadId = active?.id ?? null;

      // Make the reply box grow with the message as it's typed (after any draft
      // restore above, so a restored draft opens at the right height).
      const replyEl = threadHost.querySelector<HTMLTextAreaElement>("[data-role='reply']");
      if (replyEl) attachAutoGrow(replyEl);

      updateTabCounts();
      syncTabSelection();
      // The thread pane was just rebuilt — repaint the attach tray so any
      // pending uploads survive the re-render, and re-apply any open
      // quick-reply chips (innerHTML wiped them).
      renderAttachTray();
      renderQuickReplies();
      renderPinnedStrip();
      // Mobile: switch to thread view when one is active.
      splitHost.classList.toggle("is-thread-open", active != null);
    }

    function selectThread(id: string): void {
      activeThreadId = id;
      const thread = allThreads.find((t) => t.id === id) ?? null;
      if (thread && thread.unread > 0) {
        thread.unread = 0;
        // Best-effort server mark-read. The retailer is the 'supplier' side in
        // customer threads and the 'retailer' side in supplier threads — send
        // the matching role so the read actually sticks across polls.
        const role = thread.tab === "customers" ? "supplier" : "retailer";
        void markRead(ctx, id, role, ac.signal);
      }
      // Fetch the customer's pinned-note reminders (once per customer, cached).
      if (thread && thread.tab === "customers") void loadPinnedNotes(thread);
      rerender();
    }

    async function loadThreads(): Promise<void> {
      try {
        const threads = await fetchThreads(ctx, ownRetailerId, ac.signal);
        if (disposed || ac.signal.aborted) return;
        // Preserve any optimistic-pending bubbles by merging them in.
        const pendingByThread = new Map<string, ThreadMessage[]>();
        for (const t of allThreads) {
          const pend = t.messages.filter((m) => m.pending || m.failed);
          if (pend.length > 0) pendingByThread.set(t.id, pend);
        }
        for (const t of threads) {
          const pend = pendingByThread.get(t.id);
          if (pend && pend.length > 0) {
            t.messages = [...t.messages, ...pend.filter((p) => !t.messages.some((m) => m.id === p.id))];
            t.messages.sort((a, b) => a.ts - b.ts);
            const lm = t.messages[t.messages.length - 1];
            if (lm) t.lastTs = lm.ts;
          }
        }
        allThreads = threads;
        setBanner(null);
        rerender();
        // Honour a cross-page focus hint exactly once, after threads land.
        if (pendingContactFocus) {
          const target = pendingContactFocus.toLowerCase().trim();
          pendingContactFocus = null;
          const match = allThreads.find((t) => t.contactName.toLowerCase().trim() === target);
          if (match) {
            // Switch to the right tab in case the contact lives in a
            // non-customer thread bucket.
            if (match.tab !== activeTab) {
              activeTab = match.tab;
            }
            selectThread(match.id);
            // Auto-open Kit's competitor-aware suggestions, once.
            if (pendingSuggest && match.tab === "customers") {
              pendingSuggest = false;
              void loadQuickReplies(match);
            }
          }
        }
      } catch (err) {
        if (isAbortError(err) || ac.signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `Couldn't load messages (HTTP ${err.status})`
            : err instanceof Error
              ? err.message
              : "Couldn't load messages";
        setBanner(msg);
        // Don't blow away an existing list on transient poll failure.
        if (allThreads.length === 0) rerender();
      }
    }

    // ── Event delegation ─────────────────────────────────────────────────
    function onTabsClick(e: MouseEvent): void {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(".msg-tab");
      if (!btn) return;
      const tab = btn.dataset.tab as TabKey | undefined;
      if (!tab) return;
      if (tab === activeTab) return;
      activeTab = tab;
      activeThreadId = null;
      rerender();
    }
    tabsHost.addEventListener("click", onTabsClick);

    function onListClick(e: MouseEvent): void {
      const row = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-thread-id]");
      if (!row) return;
      const id = row.dataset.threadId;
      if (id) selectThread(id);
    }
    listRows.addEventListener("click", onListClick);

    function onListKey(e: KeyboardEvent): void {
      if (e.key !== "Enter" && e.key !== " ") return;
      const row = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-thread-id]");
      if (!row) return;
      e.preventDefault();
      const id = row.dataset.threadId;
      if (id) selectThread(id);
    }
    listRows.addEventListener("keydown", onListKey);

    function onSearchInput(): void {
      searchQuery = searchInput.value;
      rerender();
    }
    searchInput.addEventListener("input", onSearchInput);

    async function sendReplyForActive(): Promise<void> {
      if (!activeThreadId) return;
      const thread = allThreads.find((t) => t.id === activeThreadId);
      if (!thread) return;
      const replyEl = threadHost.querySelector<HTMLTextAreaElement>("[data-role='reply']");
      if (!replyEl) return;
      const body = replyEl.value.trim();
      const attachments = pendingAttachments.get(thread.id) ?? [];
      // Allow image-only / pdf-only sends (no body required when there's an attachment).
      if (!body && attachments.length === 0) return;

      // Optimistic insert.
      const tmpId = safeId();
      const optimistic: ThreadMessage = {
        id: tmpId,
        threadId: thread.id,
        from: "me",
        text: body,
        ts: Date.now(),
        channel: "in_app",
        pending: true,
        attachments: attachments.length > 0 ? [...attachments] : [],
      };
      thread.messages.push(optimistic);
      thread.lastTs = optimistic.ts;
      replyEl.value = "";
      pendingAttachments.delete(thread.id);
      rerender();

      try {
        const sent = await postReply(
          ctx,
          { thread, body, ownRetailerId, retailerName, attachments },
          ac.signal,
        );
        if (disposed || ac.signal.aborted) return;
        const idx = thread.messages.findIndex((m) => m.id === tmpId);
        if (idx >= 0) {
          if (sent) {
            thread.messages[idx] = {
              ...optimistic,
              id: String(sent.id ?? tmpId),
              ts: coerceTs(sent.createdAt ?? sent.ts ?? optimistic.ts),
              pending: false,
            };
            ctx.track({ eventType: "action", feature: "messages", customData: { action: "message-sent", tab: thread.tab } });
          } else if (thread.tab === "team") {
            // No backend — leave as confirmed local.
            const live = thread.messages[idx];
            if (live) {
              live.pending = false;
              ctx.showToast("Team chat is local-only for now", "info");
            }
          } else {
            const live = thread.messages[idx];
            if (live) live.pending = false;
          }
        }
        rerender();
      } catch (err) {
        if (isAbortError(err) || ac.signal.aborted) return;
        const idx = thread.messages.findIndex((m) => m.id === tmpId);
        if (idx >= 0) {
          const live = thread.messages[idx];
          if (live) {
            live.pending = false;
            live.failed = true;
          }
        }
        const msg =
          err instanceof ApiError
            ? `Send failed (HTTP ${err.status})`
            : err instanceof Error
              ? err.message
              : "Send failed";
        ctx.showToast(msg, "error");
        rerender();
      }
    }

    async function deleteActiveThread(): Promise<void> {
      if (!activeThreadId) return;
      const thread = allThreads.find((t) => t.id === activeThreadId);
      if (!thread) return;
      const ok = await confirmDialog(`Delete the conversation with ${thread.contactName}?`, {
        title: "Delete conversation",
        confirmLabel: "Delete",
        destructive: true,
      });
      if (!ok) return;
      const id = thread.id;
      // Optimistic local removal.
      allThreads = allThreads.filter((t) => t.id !== id);
      activeThreadId = null;
      rerender();
      try {
        await deleteThread(ctx, id, ac.signal);
        ctx.showToast("Thread deleted", "success");
      } catch (err) {
        if (isAbortError(err) || ac.signal.aborted) return;
        ctx.showToast("Couldn't delete thread on server — will retry on next sync", "error");
      }
    }

    function onThreadClick(e: MouseEvent): void {
      const target = e.target as HTMLElement | null;
      // Image attachments open in the in-portal lightbox (prev/next cycles all
      // image attachments in this thread, in chronological order). Non-image
      // attachments keep their download / new-tab anchor behaviour.
      const imgLink = target?.closest<HTMLAnchorElement>(".msg-attach--image");
      if (imgLink) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        const links = Array.from(threadHost.querySelectorAll<HTMLAnchorElement>(".msg-attach--image"));
        const images: LightboxImage[] = links.map((el) => ({
          url: el.getAttribute("href") || el.href,
          name: el.getAttribute("aria-label") || "Image",
        }));
        openLightbox({ images, startIndex: Math.max(0, links.indexOf(imgLink)) });
        return;
      }
      const action = target?.closest<HTMLElement>("[data-action]");
      if (action) {
        const a = action.dataset.action;
        if (a === "delete-thread") {
          void deleteActiveThread();
          return;
        }
        if (a === "back") {
          activeThreadId = null;
          rerender();
          return;
        }
        if (a === "attach") {
          const input = threadHost.querySelector<HTMLInputElement>(
            "[data-role='attach-input']",
          );
          input?.click();
          return;
        }
        if (a === "templates") {
          void openTemplatePicker(action);
          return;
        }
        if (a === "suggest-replies") {
          const thread = allThreads.find((t) => t.id === activeThreadId);
          if (!thread) return;
          // Toggle: if this thread's chips are already open (and not loading),
          // collapse them; otherwise fetch/show.
          if (quickRepliesFor === thread.id && !quickReplyLoading) {
            quickRepliesFor = null;
            renderQuickReplies();
          } else {
            void loadQuickReplies(thread);
          }
          return;
        }
      }
      // Insert a tapped quick-reply into the reply box (editable, not sent).
      const quickChip = target?.closest<HTMLButtonElement>("[data-quick-idx]");
      if (quickChip && activeThreadId) {
        const thread = allThreads.find((t) => t.id === activeThreadId);
        const replyEl = threadHost.querySelector<HTMLTextAreaElement>("[data-role='reply']");
        if (thread && replyEl) {
          const key = quickReplyKey(thread);
          const suggestions = key ? quickReplyCache.get(key) : undefined;
          const text = suggestions?.[Number(quickChip.dataset["quickIdx"])];
          if (text) {
            replyEl.value = replyEl.value.trim()
              ? replyEl.value.replace(/\s*$/, "") + "\n" + text
              : text;
            replyEl.dispatchEvent(new Event("input", { bubbles: true }));
            replyEl.focus();
            quickRepliesFor = null; // collapse after pick
            renderQuickReplies();
          }
        }
        return;
      }
      // Remove a queued (not-yet-sent) attachment from the tray.
      const removeBtn = target?.closest<HTMLButtonElement>(
        "[data-role='attach-remove']",
      );
      if (removeBtn && activeThreadId) {
        const id = removeBtn.dataset["attachId"];
        if (!id) return;
        const list = pendingAttachments.get(activeThreadId) ?? [];
        pendingAttachments.set(
          activeThreadId,
          list.filter((a) => a.id !== id),
        );
        renderAttachTray();
      }
    }
    threadHost.addEventListener("click", onThreadClick);

    // ── Pinned notes (read-only reminder strip) ────────────────────────────
    function renderPinnedStrip(): void {
      const host = threadHost.querySelector<HTMLElement>("[data-role='pinned-notes']");
      if (!host) return;
      const thread = allThreads.find((t) => t.id === activeThreadId) ?? null;
      const pins = thread ? pinnedNotesCache.get(thread.contactId) : undefined;
      if (!thread || !pins || pins.length === 0) {
        host.hidden = true;
        host.innerHTML = "";
        return;
      }
      host.hidden = false;
      host.innerHTML = `
        <span class="msg-pinned-notes__icon" aria-hidden="true">${iconMarkup("pin", { size: "xs" })}</span>
        <span class="msg-pinned-notes__items">${pins
          .map((p) => `<span class="msg-pinned-note">${escapeHtml(p)}</span>`)
          .join("")}</span>`;
    }

    async function loadPinnedNotes(thread: Thread): Promise<void> {
      if (thread.tab !== "customers" || !thread.contactId) return;
      if (pinnedNotesCache.has(thread.contactId)) {
        renderPinnedStrip();
        return;
      }
      try {
        const r = await ctx.api<{ ok: boolean; pins: Array<{ text: string }> }>(
          `/portal/customer-pinned-notes?customerId=${encodeURIComponent(thread.contactId)}`,
          { signal: ac.signal },
        );
        if (disposed || ac.signal.aborted) return;
        pinnedNotesCache.set(
          thread.contactId,
          Array.isArray(r.pins) ? r.pins.map((p) => p.text).filter(Boolean) : [],
        );
      } catch (err) {
        if (isAbortError(err) || ac.signal.aborted) return;
        // Non-critical reminder strip — fail silent, cache empty so we don't retry-loop.
        pinnedNotesCache.set(thread.contactId, []);
      }
      renderPinnedStrip();
    }

    // ── Quick-reply suggestions (Kit) ──────────────────────────────────────
    // Only valid when it's the retailer's turn on a customer thread. The key
    // ties a suggestion set to the specific inbound message it answers.
    function quickReplyKey(thread: Thread): string | null {
      const last = thread.messages[thread.messages.length - 1];
      if (!last || last.from !== "them") return null;
      return `${thread.id}:${last.ts}`;
    }

    function renderQuickReplies(): void {
      const host = threadHost.querySelector<HTMLElement>("[data-role='quick-replies']");
      if (!host) return;
      const thread = allThreads.find((t) => t.id === activeThreadId) ?? null;
      if (!thread || quickRepliesFor !== thread.id) {
        host.hidden = true;
        host.innerHTML = "";
        return;
      }
      if (quickReplyLoading) {
        host.hidden = false;
        host.innerHTML = `<div class="msg-quick-replies__status">${iconMarkup("sparkle", { size: "xs" })} Kit is drafting some replies…</div>`;
        return;
      }
      const key = quickReplyKey(thread);
      const suggestions = key ? quickReplyCache.get(key) : undefined;
      if (!suggestions) {
        host.hidden = true;
        host.innerHTML = "";
        return;
      }
      if (suggestions.length === 0) {
        host.hidden = false;
        host.innerHTML = `<div class="msg-quick-replies__status">No quick replies for this one — best handled in your own words.</div>`;
        return;
      }
      host.hidden = false;
      host.innerHTML = `
        <div class="msg-quick-replies__head">
          ${iconMarkup("sparkle", { size: "xs" })}
          <span>Suggested replies — tap to use, then edit before sending</span>
        </div>
        <div class="msg-quick-replies__chips">
          ${suggestions
            .map(
              (s, i) =>
                `<button type="button" class="msg-quick-chip" data-quick-idx="${i}">${escapeHtml(s)}</button>`,
            )
            .join("")}
        </div>`;
    }

    async function loadQuickReplies(thread: Thread): Promise<void> {
      const key = quickReplyKey(thread);
      if (!key) return;
      quickRepliesFor = thread.id;
      if (quickReplyCache.has(key)) {
        renderQuickReplies();
        return;
      }
      quickReplyLoading = true;
      renderQuickReplies();
      try {
        const last = thread.messages[thread.messages.length - 1];
        // A little prior context (most recent first-from-them/last-from-me
        // exchanges) — body text only, never any contact detail.
        const history = thread.messages
          .slice(-7, -1)
          .map((m) => ({ from: m.from, text: m.text }));
        const r = await ctx.api<{ ok: boolean; suggestions: string[] }>(
          "/portal/kit/suggest-replies",
          {
            method: "POST",
            signal: ac.signal,
            body: JSON.stringify({
              lastMessage: last?.text ?? "",
              history,
              customerName: thread.contactName,
              retailerName,
              subject: thread.subject,
            }),
          },
        );
        if (disposed || ac.signal.aborted) return;
        quickReplyCache.set(key, Array.isArray(r.suggestions) ? r.suggestions.slice(0, 3) : []);
        ctx.track({ eventType: "action", feature: "messages", customData: { action: "quick-replies-suggested" } });
      } catch (err) {
        if (isAbortError(err) || ac.signal.aborted) return;
        quickRepliesFor = null;
        ctx.showToast("Kit couldn't suggest replies just now — try again", "error");
      } finally {
        quickReplyLoading = false;
        renderQuickReplies();
      }
    }

    // ── Message-templates picker ───────────────────────────────────────────
    // A "Templates" button in the composer opens this popover; clicking a
    // template inserts its TEXT into the reply box (editable before sending).
    // Templates carry the retailer's own wording only — no customer contact
    // details — and sending still routes through the masked customer-message
    // path. Tagged templates surface their pipeline stage; Kit can also call
    // /message-templates/suggest?stage= for stage-aware drafting.
    let templatePickerEl: HTMLElement | null = null;
    function closeTemplatePicker(): void {
      if (templatePickerEl) { templatePickerEl.remove(); templatePickerEl = null; }
      document.removeEventListener("click", onDocClickForPicker, true);
    }
    function onDocClickForPicker(e: MouseEvent): void {
      const tgt = e.target as HTMLElement | null;
      if (templatePickerEl && !templatePickerEl.contains(tgt) && !tgt?.closest("[data-action='templates']")) {
        closeTemplatePicker();
      }
    }
    async function openTemplatePicker(anchor: HTMLElement): Promise<void> {
      if (templatePickerEl) { closeTemplatePicker(); return; }
      const replyEl = threadHost.querySelector<HTMLTextAreaElement>("[data-role='reply']");
      if (!replyEl) return;
      const form = anchor.closest<HTMLElement>("[data-role='reply-form']") ?? anchor.parentElement;
      if (!form) return;
      const pop = document.createElement("div");
      pop.className = "msg-templates-pop";
      pop.innerHTML = `<div class="msg-templates-pop__empty">Loading templates…</div>`;
      form.appendChild(pop);
      templatePickerEl = pop;
      document.addEventListener("click", onDocClickForPicker, true);
      try {
        const r = await ctx.api<{ ok: boolean; templates: Array<{ id: string; label: string; body: string; pipeline_stage: string | null }> }>(
          "/portal/message-templates");
        if (templatePickerEl !== pop) return;
        const list = Array.isArray(r.templates) ? r.templates : [];
        if (list.length === 0) {
          pop.innerHTML = `<div class="msg-templates-pop__empty">No templates yet — add them in Settings → Message Templates.</div>`;
          return;
        }
        pop.innerHTML = `
          <div class="msg-templates-pop__head">Templates</div>
          <ul class="msg-templates-pop__list">
            ${list.map((t) => `
              <li><button type="button" class="msg-templates-pop__item" data-tpl-id="${escapeHtml(t.id)}">
                <span class="msg-templates-pop__label">${escapeHtml(t.label)}${t.pipeline_stage ? ` <em>· ${escapeHtml(t.pipeline_stage)}</em>` : ""}</span>
                <span class="msg-templates-pop__preview">${escapeHtml(t.body.slice(0, 90))}</span>
              </button></li>`).join("")}
          </ul>`;
        pop.querySelectorAll<HTMLButtonElement>("[data-tpl-id]").forEach((btn) => {
          btn.addEventListener("click", () => {
            const t = list.find((x) => x.id === btn.dataset["tplId"]);
            if (!t) return;
            replyEl.value = replyEl.value.trim() ? replyEl.value.replace(/\s*$/, "") + "\n" + t.body : t.body;
            replyEl.dispatchEvent(new Event("input", { bubbles: true }));
            replyEl.focus();
            closeTemplatePicker();
          });
        });
      } catch {
        if (templatePickerEl === pop) pop.innerHTML = `<div class="msg-templates-pop__empty">Couldn't load templates.</div>`;
      }
    }

    function renderAttachTray(): void {
      const tray = threadHost.querySelector<HTMLElement>("[data-role='attach-tray']");
      if (!tray) return;
      const items = activeThreadId
        ? pendingAttachments.get(activeThreadId) ?? []
        : [];
      if (items.length === 0) {
        tray.hidden = true;
        tray.innerHTML = "";
        return;
      }
      tray.hidden = false;
      tray.innerHTML = items
        .map((a) => {
          const previewHtml = a.kind === "image"
            ? `<img src="${escapeHtml(a.url)}" alt="${escapeHtml(a.name)}" />`
            : `<span class="msg-attach-chip__icon" aria-hidden="true">📄</span>`;
          const sizeLabel = formatBytes(a.size);
          return `
            <div class="msg-attach-chip">
              ${previewHtml}
              <span class="msg-attach-chip__name" title="${escapeHtml(a.name)}">${escapeHtml(a.name)}</span>
              ${sizeLabel ? `<span class="msg-attach-chip__size">${escapeHtml(sizeLabel)}</span>` : ""}
              <button type="button" class="msg-attach-chip__remove"
                      data-role="attach-remove"
                      data-attach-id="${escapeHtml(a.id)}"
                      aria-label="Remove ${escapeHtml(a.name)}">×</button>
            </div>
          `;
        })
        .join("");
    }

    async function onAttachInputChange(e: Event): Promise<void> {
      const input = e.target as HTMLInputElement;
      const files = Array.from(input.files ?? []);
      // Reset the input so re-picking the same file fires another change.
      input.value = "";
      if (files.length === 0 || !activeThreadId) return;
      try {
        const uploaded = await uploadAttachments(files);
        if (disposed) return;
        const list = pendingAttachments.get(activeThreadId) ?? [];
        pendingAttachments.set(activeThreadId, [...list, ...uploaded]);
        renderAttachTray();
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Upload failed.";
        ctx.showToast(msg, "error");
      }
    }
    function onThreadChange(e: Event): void {
      const target = e.target as HTMLElement | null;
      if (target?.matches?.("[data-role='attach-input']")) {
        void onAttachInputChange(e);
      }
    }
    threadHost.addEventListener("change", onThreadChange);

    function onThreadSubmit(e: SubmitEvent): void {
      const form = (e.target as HTMLElement | null)?.closest<HTMLFormElement>(
        "[data-role='reply-form']",
      );
      if (!form) return;
      e.preventDefault();
      void sendReplyForActive();
    }
    threadHost.addEventListener("submit", onThreadSubmit);

    function onThreadKeydown(e: KeyboardEvent): void {
      if (e.key !== "Enter" || e.shiftKey) return;
      const target = e.target as HTMLElement | null;
      if (!target || target.dataset.role !== "reply") return;
      e.preventDefault();
      void sendReplyForActive();
    }
    threadHost.addEventListener("keydown", onThreadKeydown);

    // ── Compose ──────────────────────────────────────────────────────────
    //
    // The recipient picker is a custom dropdown — no freetext, no IDs.
    // The user searches by name (with optional email substring match),
    // clicks a result, and the selection becomes a "chip". A "Change"
    // button restores the search input. Datasets are loaded lazily per
    // tab on first open and cached for the lifetime of the page mount;
    // each subsequent open refreshes the cache in the background so the
    // list stays current without blocking the modal.

    // Per-tab data cache. `null` until the first fetch completes; an
    // empty array means "fetched but empty".
    const pickerCache: Record<TabKey, ComposeContact[] | null> = {
      customers: null,
      suppliers: null,
      team:      null,
    };

    // Selection state — bound to whatever tab is currently active in the
    // modal. Cleared when the user changes tab or clicks "Change".
    let pickerSelection: ComposeContact | null = null;
    let pickerHighlight = -1;

    function setPickerHint(text: string): void {
      compose.pickerHint.textContent = text;
    }

    async function loadPickerData(tab: TabKey): Promise<ComposeContact[]> {
      if (pickerCache[tab] !== null) return pickerCache[tab]!;
      setPickerHint("Loading…");
      try {
        if (tab === "customers") {
          interface WireCustomer { id?: string; name?: string; email?: string }
          const resp = await ctx.api<{ contacts?: WireCustomer[] }>(
            "/crm/contacts",
            { signal: ac.signal },
          );
          const list = (Array.isArray(resp?.contacts) ? resp.contacts : [])
            .map((r): ComposeContact => ({
              id: String(r.id ?? "").trim(),
              name: String(r.name ?? "").trim(),
              email: String(r.email ?? "").trim(),
            }))
            .filter((r) => r.id && r.name);
          pickerCache.customers = list;
          return list;
        }
        if (tab === "suppliers") {
          interface WireSupplier { id?: string; name?: string; email?: string; type?: string }
          const resp = await ctx.api<{ contacts?: WireSupplier[] }>(
            "/portal/contacts?type=suppliers",
            { signal: ac.signal },
          );
          const list = (Array.isArray(resp?.contacts) ? resp.contacts : [])
            .map((r): ComposeContact => ({
              id: String(r.id ?? "").trim(),
              name: String(r.name ?? "").trim(),
              email: String(r.email ?? "").trim(),
            }))
            .filter((r) => r.id && r.name);
          pickerCache.suppliers = list;
          return list;
        }
        // team
        interface WireUser { userId?: string; name?: string; email?: string; role?: string; active?: boolean }
        const qs = new URLSearchParams({ retailerId: ownRetailerId });
        const resp = await ctx.api<{ users?: WireUser[] }>(
          `/users/list?${qs.toString()}`,
          { signal: ac.signal },
        );
        const list = (Array.isArray(resp?.users) ? resp.users : [])
          .filter((u) => u.active !== false)
          .map((r): ComposeContact => ({
            id: String(r.userId ?? "").trim(),
            name: String(r.name ?? "").trim(),
            email: String(r.email ?? "").trim(),
          }))
          .filter((r) => r.id && r.name);
        pickerCache.team = list;
        return list;
      } catch (err) {
        if (isAbortError(err)) return [];
        const tabLabel =
          tab === "customers" ? "customers"
          : tab === "suppliers" ? "suppliers"
          : "team members";
        setPickerHint(`Could not load ${tabLabel}. Try again shortly.`);
        return [];
      }
    }

    function renderPickerResults(query: string): void {
      const tab = compose.tabSelect.value as TabKey;
      const list = pickerCache[tab] ?? [];
      const q = query.trim().toLowerCase();

      // Empty-query rule: only show the list when the field is focused,
      // not the moment the modal opens. The hint tells the user what to
      // do; the dropdown appears on first keystroke.
      if (!q && document.activeElement !== compose.pickerSearch) {
        compose.pickerResults.innerHTML = "";
        compose.pickerResults.hidden = true;
        compose.pickerSearch.setAttribute("aria-expanded", "false");
        setPickerHint(
          list.length === 0
            ? "No contacts available yet."
            : `${list.length} available — start typing to search.`,
        );
        pickerHighlight = -1;
        return;
      }

      const matches = q
        ? list.filter((c) => c.name.toLowerCase().includes(q))
        : list;
      const visible = matches.slice(0, 20);

      if (visible.length === 0) {
        compose.pickerResults.innerHTML = "";
        compose.pickerResults.hidden = true;
        compose.pickerSearch.setAttribute("aria-expanded", "false");
        setPickerHint(
          list.length === 0
            ? "No contacts available yet."
            : "No matches — try a different name.",
        );
        pickerHighlight = -1;
        return;
      }

      pickerHighlight = 0;
      compose.pickerResults.innerHTML = visible
        .map((c, i) => {
          const safeName = escAttr(c.name);
          return `
            <button type="button" class="msg-picker__option${i === pickerHighlight ? " is-active" : ""}"
                    role="option" data-cid="${escAttr(c.id)}"
                    aria-selected="${i === pickerHighlight ? "true" : "false"}">
              <span class="msg-picker__option-name">${safeName}</span>
            </button>
          `;
        })
        .join("");
      compose.pickerResults.hidden = false;
      compose.pickerSearch.setAttribute("aria-expanded", "true");
      setPickerHint(
        matches.length > visible.length
          ? `Showing ${visible.length} of ${matches.length} — keep typing to narrow.`
          : "",
      );
    }

    function escAttr(s: string): string {
      return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    }

    function selectContact(c: ComposeContact): void {
      pickerSelection = c;
      compose.pickerSearchWrap.hidden = true;
      compose.pickerResults.hidden = true;
      compose.pickerSearch.setAttribute("aria-expanded", "false");
      compose.pickerSelected.hidden = false;
      compose.pickerChip.textContent = c.name;
      setPickerHint("");
    }

    function clearPickerSelection(): void {
      pickerSelection = null;
      compose.pickerSearch.value = "";
      compose.pickerSearchWrap.hidden = false;
      compose.pickerSelected.hidden = true;
      renderPickerResults("");
      setTimeout(() => compose.pickerSearch.focus(), 0);
    }

    function updateRecipientLabel(): void {
      const v = compose.tabSelect.value as TabKey;
      compose.recipientLabel.textContent =
        v === "customers" ? "Customer"
        : v === "suppliers" ? "Supplier"
        : "Team member";
      compose.pickerSearch.placeholder =
        v === "customers" ? "Search customers by name…"
        : v === "suppliers" ? "Search suppliers by name…"
        : "Search team by name…";
    }

    async function refreshPickerForTab(): Promise<void> {
      const tab = compose.tabSelect.value as TabKey;
      // Subject only applies to supplier + team messages; customer messages
      // are a plain conversation with no subject line. Hide the field (and
      // clear it) when composing to a customer.
      const showSubject = tab !== "customers";
      compose.subjectField.hidden = !showSubject;
      if (!showSubject) compose.subject.value = "";
      // Reset selection any time the tab changes (a customer is not a
      // supplier; the user must re-pick).
      clearPickerSelection();
      updateRecipientLabel();
      setPickerHint("Loading…");
      const list = await loadPickerData(tab);
      // If the user has changed tab while we were loading, bail.
      if ((compose.tabSelect.value as TabKey) !== tab) return;
      if (list.length === 1) {
        // "Auto-populate the field when only one match exists" — the
        // whole-dataset has exactly one option, so pick it immediately.
        const only = list[0];
        if (only) {
          selectContact(only);
          return;
        }
      }
      renderPickerResults("");
      compose.pickerSearch.focus();
    }

    // Attachments queued for the compose modal (separate from the per-thread
    // `pendingAttachments` map so opening compose doesn't disturb in-flight
    // thread replies).
    let composeAttachments: MessageAttachment[] = [];

    function renderComposeAttachTray(): void {
      const items = composeAttachments;
      if (items.length === 0) {
        compose.attachTray.hidden = true;
        compose.attachTray.innerHTML = "";
        return;
      }
      compose.attachTray.hidden = false;
      compose.attachTray.innerHTML = attachmentTrayHtml(items);
    }

    function openCompose(): void {
      compose.tabSelect.value = activeTab === "team" ? "team" : activeTab;
      compose.subject.value = "";
      compose.body.value = "";
      resetAutoGrow(compose.body);
      composeAttachments = [];
      renderComposeAttachTray();
      clearPickerSelection();
      if (typeof compose.dialog.showModal === "function") {
        compose.dialog.showModal();
      } else {
        compose.dialog.setAttribute("open", "");
      }
      void refreshPickerForTab();
    }

    function closeCompose(): void {
      if (compose.dialog.open) compose.dialog.close();
    }

    composeBtn.addEventListener("click", openCompose);
    compose.tabSelect.addEventListener("change", () => { void refreshPickerForTab(); });
    compose.cancelBtn.addEventListener("click", closeCompose);
    compose.pickerClear.addEventListener("click", clearPickerSelection);

    // Filter on every keystroke.
    compose.pickerSearch.addEventListener("input", () => {
      renderPickerResults(compose.pickerSearch.value);
    });
    // Re-open the dropdown when the field regains focus (user clicked
    // back into the search after dismissing).
    compose.pickerSearch.addEventListener("focus", () => {
      renderPickerResults(compose.pickerSearch.value);
    });

    // Keyboard navigation: ↓/↑ move highlight, Enter selects, Escape closes
    // the dropdown. Tab to leave the field falls through to the next field.
    compose.pickerSearch.addEventListener("keydown", (e: KeyboardEvent) => {
      const optionEls = compose.pickerResults.querySelectorAll<HTMLButtonElement>(".msg-picker__option");
      if (optionEls.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        pickerHighlight = (pickerHighlight + 1) % optionEls.length;
        applyPickerHighlight(optionEls);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        pickerHighlight = (pickerHighlight - 1 + optionEls.length) % optionEls.length;
        applyPickerHighlight(optionEls);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const chosen = optionEls[pickerHighlight] ?? optionEls[0];
        chosen?.click();
      } else if (e.key === "Escape") {
        if (!compose.pickerResults.hidden) {
          e.preventDefault();
          compose.pickerResults.hidden = true;
          compose.pickerSearch.setAttribute("aria-expanded", "false");
        }
      }
    });

    function applyPickerHighlight(els: NodeListOf<HTMLButtonElement>): void {
      els.forEach((el, i) => {
        el.classList.toggle("is-active", i === pickerHighlight);
        el.setAttribute("aria-selected", i === pickerHighlight ? "true" : "false");
        if (i === pickerHighlight) el.scrollIntoView({ block: "nearest" });
      });
    }

    // Click on an option commits the selection.
    compose.pickerResults.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(".msg-picker__option");
      if (!btn) return;
      const cid = btn.dataset["cid"];
      const tab = compose.tabSelect.value as TabKey;
      const list = pickerCache[tab] ?? [];
      const match = list.find((c) => c.id === cid);
      if (match) selectContact(match);
    });

    // Click outside the picker (but still inside the modal) closes the
    // results dropdown without clearing the search text.
    compose.dialog.addEventListener("click", (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (t.closest(".msg-picker")) return;
      if (!compose.pickerResults.hidden) {
        compose.pickerResults.hidden = true;
        compose.pickerSearch.setAttribute("aria-expanded", "false");
      }
    });

    const onComposeBackdrop = (e: MouseEvent): void => {
      if (e.target === compose.dialog) closeCompose();
    };
    compose.dialog.addEventListener("click", onComposeBackdrop);
    const onComposeClose = (e: MouseEvent): void => {
      const a = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-action='close']");
      if (a) closeCompose();
    };
    compose.dialog.addEventListener("click", onComposeClose);

    // ── Compose attachment wiring ───────────────────────────────────────
    compose.attachBtn.addEventListener("click", () => compose.attachInput.click());
    const onComposeAttachChange = async (): Promise<void> => {
      const files = Array.from(compose.attachInput.files ?? []);
      compose.attachInput.value = "";
      if (files.length === 0) return;
      try {
        const uploaded = await uploadAttachments(files);
        if (disposed) return;
        composeAttachments = [...composeAttachments, ...uploaded];
        renderComposeAttachTray();
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Upload failed.";
        ctx.showToast(msg, "error");
      }
    };
    compose.attachInput.addEventListener("change", () => { void onComposeAttachChange(); });

    // Drag-and-drop on the row + tray.
    const stopDnd = (e: DragEvent): void => { e.preventDefault(); e.stopPropagation(); };
    for (const target of [compose.dropZone, compose.attachTray]) {
      (["dragenter", "dragover"] as const).forEach((evt) => {
        target.addEventListener(evt, (e) => {
          stopDnd(e as DragEvent);
          target.classList.add("is-dragover");
        });
      });
      (["dragleave", "dragend"] as const).forEach((evt) => {
        target.addEventListener(evt, (e) => {
          stopDnd(e as DragEvent);
          target.classList.remove("is-dragover");
        });
      });
      target.addEventListener("drop", (e) => {
        stopDnd(e);
        target.classList.remove("is-dragover");
        const list = e.dataTransfer?.files;
        if (!list || list.length === 0) return;
        void (async () => {
          try {
            const uploaded = await uploadAttachments(Array.from(list));
            if (disposed) return;
            composeAttachments = [...composeAttachments, ...uploaded];
            renderComposeAttachTray();
          } catch (err) {
            const msg = err instanceof Error ? err.message : "Upload failed.";
            ctx.showToast(msg, "error");
          }
        })();
      });
    }

    // Remove chip × — delegated on the tray.
    compose.attachTray.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(
        "[data-role='attach-remove']",
      );
      if (!btn) return;
      const id = btn.dataset["attachId"];
      if (!id) return;
      composeAttachments = composeAttachments.filter((a) => a.id !== id);
      renderComposeAttachTray();
    });

    async function handleComposeSubmit(e: SubmitEvent): Promise<void> {
      e.preventDefault();
      const tab = compose.tabSelect.value as TabKey;
      const subject = compose.subject.value.trim();
      const body = compose.body.value.trim();
      if (!pickerSelection) {
        setPickerHint("Select a recipient before sending.");
        compose.pickerSearch.focus();
        return;
      }
      // Subject is required for supplier + team messages, but never shown
      // (or required) for customer messages.
      if (tab !== "customers" && !subject) return;
      // Allow attachment-only sends if there are attachments queued.
      if (!body && composeAttachments.length === 0) return;
      try {
        const sent = await postCompose(
          ctx,
          {
            tab,
            recipient: pickerSelection,
            subject,
            body,
            ownRetailerId,
            retailerName,
            attachmentIds: composeAttachments.map((a) => a.id),
          },
          ac.signal,
        );
        if (disposed || ac.signal.aborted) return;
        if (sent || tab === "team") {
          ctx.showToast("Message sent", "success");
          closeCompose();
          activeTab = tab;
          // Pull fresh list so the new thread shows up.
          await loadThreads();
        } else {
          ctx.showToast("Message accepted but no thread returned", "info");
          closeCompose();
        }
      } catch (err) {
        if (isAbortError(err) || ac.signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `Send failed (HTTP ${err.status})`
            : err instanceof Error
              ? err.message
              : "Send failed";
        ctx.showToast(msg, "error");
      }
    }
    compose.form.addEventListener("submit", handleComposeSubmit);

    // ── Polling (3s) with visibility-pause ───────────────────────────────
    let pollHandle: ReturnType<typeof setInterval> | null = null;
    function startPolling(): void {
      if (pollHandle != null) return;
      pollHandle = setInterval(() => {
        if (document.visibilityState === "hidden") return; // belt-and-braces
        void loadThreads();
      }, POLL_INTERVAL_MS);
    }
    function stopPolling(): void {
      if (pollHandle != null) {
        clearInterval(pollHandle);
        pollHandle = null;
      }
    }
    function onVisibilityChange(): void {
      if (document.visibilityState === "hidden") {
        stopPolling();
      } else {
        // Refresh immediately, then resume polling.
        void loadThreads();
        startPolling();
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange);

    // Initial paint + first fetch.
    rerender();
    void loadThreads();
    startPolling();

    // Live customer→retailer (and supplier→retailer) messages: refresh the
    // thread list the moment the server pushes `message.received`, instead of
    // waiting for the 30s poll. loadThreads() preserves optimistic bubbles.
    const offMessageReceived = realtime.onEvent("message.received", () => {
      if (disposed) return;
      void loadThreads();
    });

    // ── Cleanup ──────────────────────────────────────────────────────────
    return (): void => {
      disposed = true;
      ac.abort();
      offMessageReceived();
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      tabsHost.removeEventListener("click", onTabsClick);
      listRows.removeEventListener("click", onListClick);
      listRows.removeEventListener("keydown", onListKey);
      searchInput.removeEventListener("input", onSearchInput);
      threadHost.removeEventListener("click", onThreadClick);
      threadHost.removeEventListener("change", onThreadChange);
      threadHost.removeEventListener("submit", onThreadSubmit);
      threadHost.removeEventListener("keydown", onThreadKeydown);
      composeBtn.removeEventListener("click", openCompose);
      compose.tabSelect.removeEventListener("change", updateRecipientLabel);
      compose.cancelBtn.removeEventListener("click", closeCompose);
      compose.dialog.removeEventListener("click", onComposeBackdrop);
      compose.dialog.removeEventListener("click", onComposeClose);
      compose.form.removeEventListener("submit", handleComposeSubmit);
      if (compose.dialog.open) compose.dialog.close();
    };
  },
};

registerPage(messagesPage);

export default messagesPage;
